import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  rangeControl,
  stat,
  statRow,
} from "../ui/components";
import { plot, PALETTE } from "../ui/charts";
import { heatGrid } from "../ui/grid";
import { themeColors, h2, legend, caption, ticker } from "./shared";
import { MinHash } from "../core/minhash";
import { formatPercent } from "../ui/format";
import type { PageRenderer } from "../ui/router";

const K = 256; // full signature length; smaller k is just a prefix of this

export const renderMinHash: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Similarity · near-duplicates",
      title: "MinHash + LSH",
      lede: "Estimate how similar two sets are from short signatures, and find near-duplicates among millions without comparing every pair. Edit two documents and watch the estimate converge on the true Jaccard similarity.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "For a single hash function, the probability that two sets share the same minimum hash value is ",
        el("em", {}, "exactly"),
        " their Jaccard similarity |A ∩ B| / |A ∪ B|. So hash each set k ways, keep the minimum per function, and the fraction of matching signature slots is an unbiased estimate of similarity — with error shrinking like ",
        el("strong", {}, "√(J(1−J)/k)"),
        ". The sets never have to meet; only their tiny signatures do.",
      ),
    ),
  );

  similarityCard(outlet);
  lshCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { MinHash, MinHashLSH } from "./core";

const a = new MinHash({ numHashes: 128 });
shingles(docA).forEach((s) => a.add(s));
const b = new MinHash({ numHashes: 128 });
shingles(docB).forEach((s) => b.add(s));

a.jaccard(b); // ≈ Jaccard similarity of the two documents

// Scale to millions of docs: LSH surfaces only the similar candidates.
const lsh = new MinHashLSH({ bands: 32, rows: 4 }); // threshold ≈ 0.42
lsh.insert("docA", a);
lsh.query(b); // → ["docA"] if similar enough`,
    ),
  );

  append(
    outlet,
    callout(
      "info",
      el(
        "div",
        {},
        el("strong", {}, "From pairs to planet-scale. "),
        "Comparing every pair of N documents is N² — hopeless at scale. LSH hashes signature slices into buckets so that only ",
        el("em", {}, "similar"),
        " items are likely to collide, turning near-duplicate search into a handful of bucket lookups.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------

function tokens(text: string): Set<string> {
  return new Set(text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
}

function trueJaccard(a: Set<string>, b: Set<string>): number {
  let inter = 0;
  for (const x of a) if (b.has(x)) inter++;
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

function similarityCard(outlet: HTMLElement): void {
  const textA = signal("the quick brown fox jumps over the lazy dog");
  const textB = signal("the quick brown cat jumps over the lazy dog");
  const kSig = signal(64);
  const { tick, bump } = ticker();

  let sigA = new MinHash({ numHashes: K });
  let sigB = new MinHash({ numHashes: K });
  let jTrue = 0;

  // Rebuild signatures when either text changes.
  effect(() => {
    const a = tokens(textA.value);
    const b = tokens(textB.value);
    sigA = new MinHash({ numHashes: K });
    sigB = new MinHash({ numHashes: K });
    a.forEach((s) => sigA.add(s));
    b.forEach((s) => sigB.add(s));
    jTrue = trueJaccard(a, b);
    bump();
  });

  function estimateAt(k: number): number {
    let equal = 0;
    for (let i = 0; i < k; i++) if (sigA.signature[i] === sigB.signature[i]) equal++;
    return equal / k;
  }

  const stripHost = el("div", { class: "viz-wrap" });
  const chartHost = el("div", { class: "viz-wrap" });

  effect(() => {
    tick.value;
    const c = themeColors();
    const k = kSig.value;

    // Signature-agreement strip for the first min(k,128) slots.
    const show = Math.min(k, 128);
    const cols = Math.min(show, 64);
    const rows = Math.ceil(show / cols);
    const grid = heatGrid(cols, rows, { cellSize: 12, gap: 2 });
    stripHost.replaceChildren(grid.canvas);
    grid.draw((i) => (i < show && sigA.signature[i] === sigB.signature[i] ? c.accent : c.border));

    // Convergence: estimate vs k, with the true value as a reference line.
    const pts: Array<[number, number]> = [];
    for (let kk = 2; kk <= K; kk = Math.ceil(kk * 1.25)) pts.push([kk, estimateAt(kk)]);
    chartHost.replaceChildren(
      plot({
        height: 280,
        xScale: "log",
        xLabel: "number of hashes (k)",
        yLabel: "estimated Jaccard",
        yDomain: [0, 1],
        xFormat: (v) => String(v),
        yFormat: (v) => v.toFixed(1),
        series: [
          { type: "line", points: [[2, jTrue], [K, jTrue]], color: c.textFaint, dashed: true, label: "true J" },
          { type: "line", points: pts, color: PALETTE[0], label: "estimate" },
          { type: "scatter", points: [[k, estimateAt(k)]], color: PALETTE[2], radius: 5 },
        ],
        legend: true,
      }),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    el(
      "label",
      { class: "control" },
      el("span", { class: "control-label" }, "Document A"),
      el("textarea", {
        class: "textinput",
        rows: "3",
        oninput: (e: Event) => (textA.value = (e.target as HTMLTextAreaElement).value),
      }, "the quick brown fox jumps over the lazy dog"),
    ),
    el(
      "label",
      { class: "control" },
      el("span", { class: "control-label" }, "Document B"),
      el("textarea", {
        class: "textinput",
        rows: "3",
        oninput: (e: Event) => (textB.value = (e.target as HTMLTextAreaElement).value),
      }, "the quick brown cat jumps over the lazy dog"),
    ),
    rangeControl({ label: "hashes k", min: 2, max: K, signal: kSig, format: (v) => String(v) }),
    statRow(
      stat({ label: "True Jaccard", value: () => (tick.value, formatPercent(jTrue)) }),
      stat({ label: "Estimate", accent: true, value: () => (tick.value, formatPercent(estimateAt(kSig.value))) }),
    ),
    stat({
      label: "Std error at this k",
      value: () => (tick.value, formatPercent(Math.sqrt((jTrue * (1 - jTrue)) / kSig.value))),
      hint: "√(J(1−J)/k) — shrinks as k grows",
    }),
  );

  const viz = el(
    "div",
    {},
    stripHost,
    legend([[themeColors().accent, "slots that agree (→ estimate)"], [themeColors().border, "slots that differ"]]),
    caption("Each cell compares one signature slot of A vs B. The green fraction is the similarity estimate."),
    chartHost,
  );

  append(
    outlet,
    card({
      title: "Estimate document similarity",
      subtitle: "Edit either document. The estimate converges on the true Jaccard as you add hashes.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}

// ---------------------------------------------------------------------------

function lshCard(outlet: HTMLElement): void {
  const bands = signal(16);
  const rows = signal(4);
  const chartHost = el("div", { class: "viz-wrap" });

  effect(() => {
    const b = bands.value;
    const r = rows.value;
    const pts: Array<[number, number]> = [];
    for (let s = 0; s <= 1.0001; s += 0.02) pts.push([s, 1 - Math.pow(1 - Math.pow(s, r), b)]);
    const threshold = Math.pow(1 / b, 1 / r);
    const c = themeColors();
    chartHost.replaceChildren(
      plot({
        height: 300,
        xLabel: "actual similarity s",
        yLabel: "P(becomes a candidate)",
        xDomain: [0, 1],
        yDomain: [0, 1],
        xFormat: (v) => v.toFixed(1),
        yFormat: (v) => v.toFixed(1),
        series: [
          { type: "line", points: pts, color: PALETTE[1], strokeWidth: 2.5, label: `${b} bands × ${r} rows` },
          { type: "line", points: [[threshold, 0], [threshold, 1]], color: c.accent, dashed: true, label: `threshold ≈ ${threshold.toFixed(2)}` },
        ],
        legend: true,
      }),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    rangeControl({ label: "bands b", min: 1, max: 50, signal: bands, format: (v) => String(v) }),
    rangeControl({ label: "rows per band r", min: 1, max: 12, signal: rows, format: (v) => String(v) }),
    statRow(
      stat({ label: "Signature length", value: () => String(bands.value * rows.value), hint: "bands × rows" }),
      stat({ label: "Threshold", accent: true, value: () => (1 / bands.value) ** (1 / rows.value) < 1 ? formatPercent(Math.pow(1 / bands.value, 1 / rows.value)) : "—" }),
    ),
    el("p", { class: "mini-note" }, "The band/row split turns the gentle similarity signal into a sharp S-curve: pairs above the threshold almost always become candidates; pairs below almost never do."),
  );

  append(
    outlet,
    card({
      title: "Tune the LSH threshold",
      subtitle: "Splitting a signature into b bands of r rows sets a similarity cutoff at ≈ (1/b)^(1/r). Steeper curves mean cleaner separation.",
      children: [el("div", { class: "lab-layout" }, controls, chartHost)],
    }),
  );
}
